/**
 * 법규 관리 — **읽기 전용 화면**이다.
 *
 * ## 손으로 등록·수정·삭제하는 컨트롤을 다시 넣지 말 것
 *
 * 2026-10-25에 쓰기 컨트롤 다섯(툴바 「+ 법규 등록」·「초기화」, 행의 「수정」·「삭제」,
 * 「일괄 삭제」)과 그 둘째 진입점(상세 모달의 「수정」)을 걷었다. 근거는 셋이고 **각각 독립이다**:
 *
 * 1. **서버에 그 라우트가 하나도 없었다** — 다섯 컨트롤이 치던 경로가 전부 404였다.
 *    `admin.controller`의 `regulations` 라우트는 `GET` 넷(목록·stats·types·상세) +
 *    `POST regulations/sync` 다섯뿐이다. 즉 이 화면의 쓰기는 **한 번도 동작한 적이 없다.**
 * 2. **법령 본문의 정본은 법제처(국가법령정보) API**이고 우리 쪽 쓰기 수단은 수집
 *    (`adminApi.syncRegulations` → `POST /admin/regulations/sync`) 하나다. 이 화면이 보여주는
 *    `regulations`는 **경로 이름일 뿐이고 `Regulation` 모델은 존재하지 않는다** — 서버
 *    `admin-regulations.service`가 만지는 것은 `law`·`lawArticle`이다.
 * 3. **`isActive`의 소유자는 서버의 `reevaluateLawVersionFlags`다**(수집이 끝날 때마다 전 그룹을
 *    재평가해 같은 법령의 여러 시행본 중 현행 하나만 true로 둔다). 그래서 손으로 토글하면
 *    **다음 수집이 되돌린다** — 걷어낸 등록·수정 모달의 「활성 상태」 체크박스가 그 충돌이었다.
 *
 * 되짚을 조건: 서버에 쓰기 라우트가 **생기고** 그 쓰기가 위 3의 재평가와 어떻게 공존하는지
 * (수동 값을 보존하나 덮나) 정해졌을 때. 그 둘 중 하나라도 없으면 컨트롤만 되살리는 것은
 * 404 또는 조용히 덮이는 입력으로 돌아간다. **그 조건의 정본은 결정 기록
 * `docs/decisions/2026-10-25-admin-regulation-screen-is-read-only.md`다** — 기각한 대안 넷과
 * 「수집기가 덮지 않는 별 출처 표식이 먼저 필요하다」는 재료가 거기 있다. 여기 베껴 적지
 * 않는 것은 두 자리에 적으면 한쪽이 낡기 때문이다.
 *
 * 남은 쓰기는 **수집·임베딩 셋**(업데이트 / 조문 임베딩 / 호 임베딩)뿐이고, 그 외 경로는 전부
 * 조회다. 버전 필터(`activeFilter`) 기본값의 탐지기는
 * `__tests__/regulationsVersionFilterDefault.test.ts`, 이 화면에 쓰기 컨트롤이 없다는 것의
 * 탐지기는 `__tests__/regulationsReadOnly.test.ts`다.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { Button, Card, Modal } from '@safetyhub/ui';
import { adminApi, extractApiError } from '../lib/api';
import { useToastStore } from '../stores/toast';
import LawAnnexesModal from '../components/regulations/LawAnnexesModal';
import AdminRulesModal from '../components/regulations/AdminRulesModal';
import AnnexUnmappedSymbolsModal from '../components/regulations/AnnexUnmappedSymbolsModal';

interface Regulation {
  id: string;
  type: string;
  code: string | null;
  article: string | null;
  title: string | null;
  summary: string | null;
  fullText?: string | null;
  url: string | null;
  effectiveDate: string | null;
  isActive: boolean;
  knowledgeLinkCount: number;
  findingLinkCount: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * 법령 버전 판정의 상주 신호 — 서버 `admin-regulations.service`의 `LawVersionSignals`가 정본이고
 * 봉투 타입은 `lib/api.ts`의 `getRegulationStats`다(그 docblock에 두 뜻·단위·optional 사유가 있다).
 *
 * ⚠️ optional이다 — `deploy-ec2`/`deploy-admin` 순서가 보장되지 않아 이 칸이 없는 응답을 읽는
 * 창이 있다. `?.`를 떼지 말 것.
 */
interface LawVersionSignals {
  upcoming: number;
  skippedNoGroupKey: number;
  skippedNoEnforcementDate: number;
  skippedIncoherentGroups: number;
  skippedNoCurrentCandidate: number;
}

interface RegulationStats {
  total: number;
  active: number;
  byType: Array<{ type: string; count: number }>;
  linkedToKnowledge: number;
  linkedToFindings: number;
  versionSignals?: LawVersionSignals;
}

export default function Regulations() {
  const showToast = useToastStore((state) => state.showToast);

  // 목록 상태
  const [regulations, setRegulations] = useState<Regulation[]>([]);
  const [stats, setStats] = useState<RegulationStats | null>(null);
  const [regulationTypes, setRegulationTypes] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // 필터
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  // 기본은 **현행만**(2026-10-25 사용자 결정 — 「기본은 현행만 + 옛 버전 보기 토글」).
  //
  // ⚠️ 이 기본값을 **서버로 옮기지 말 것.** `admin-regulations.service`의 `list`는
  // `isActive`가 `undefined`일 때 「필터 없음 = 전체」로 읽고, DTO 타입이 `boolean?`이라
  // **「전체」를 표현하는 유일한 방법이 `undefined`**다. 서버 기본값을 「현행만」으로 바꾸면
  // 아래 `select`의 「전체」가 아무것도 못 보내게 되어 **옛 버전을 영영 볼 수 없다**.
  // 그래서 기본값은 **보내는 쪽**이 소유한다. 되짚을 조건: DTO에 「전체」를 뜻하는 별 값이
  // 생기면 그때 서버로 내릴 수 있다.
  //
  // 이 기본값의 탐지기는 `__tests__/regulationsVersionFilterDefault.test.ts`다.
  const [activeFilter, setActiveFilter] = useState<boolean | undefined>(true);
  const [sortBy, setSortBy] = useState<'type' | 'code' | 'createdAt' | 'effectiveDate'>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // 동기화/임베딩
  const [syncLoading, setSyncLoading] = useState(false);
  const [serverBusy, setServerBusy] = useState<'sync' | 'embedding' | null>(null);
  const [embeddingProgress, setEmbeddingProgress] = useState<{ processed: number; total: number } | null>(null);
  const statusPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // 모달 — 조회뿐이다(상세 보기). 편집·초기화 모달은 2026-10-25에 걷었다(위 머리 주석).
  const [detailModal, setDetailModal] = useState<Regulation | null>(null);
  // 별표 열람(S1 · 2026-11-01) — 읽기 전용. 요청은 `LawAnnexesModal`이 친다(이 페이지의 adminApi 호출 전수는 그대로다 —
  // `regulationsReadOnly.test.ts` 구조 축).
  const [annexesOpen, setAnnexesOpen] = useState(false);
  // 고시 열람(②-f · 2026-11-02) — 읽기 전용. 요청은 `AdminRulesModal`이 친다(별표 모달과 같은 이유로 이 페이지의 adminApi 호출 전수는 그대로다)
  const [adminRulesOpen, setAdminRulesOpen] = useState(false);
  // 별표 복원의 「대응 안 된 기호」(2026-11-03) — 읽기 전용. 요청은 `AnnexUnmappedSymbolsModal`이 친다(위 둘과 같은 이유)
  const [unmappedSymbolsOpen, setUnmappedSymbolsOpen] = useState(false);

  // 서버 상태 확인 (동기화/임베딩)
  const checkServerStatus = useCallback(async (): Promise<'sync' | 'embedding' | null> => {
    try {
      const res = await adminApi.getEmbeddingStatus();
      const d = res.data;
      if (!d) return null;
      if (d.sync) {
        setEmbeddingProgress(null);
        return 'sync';
      }
      if (d.regulation || d.knowledge || d.lawArticle || d.lawSubItem) {
        setEmbeddingProgress(d.regulationProgress || d.knowledgeProgress || d.lawArticleProgress || d.lawSubItemProgress || null);
        return 'embedding';
      }
      setEmbeddingProgress(null);
      return null;
    } catch {
      return null;
    }
  }, []);

  // 상태 폴링 시작/중지
  const startStatusPoll = useCallback(() => {
    if (statusPollRef.current) return;
    statusPollRef.current = setInterval(async () => {
      const status = await checkServerStatus();
      setServerBusy(status);
      if (!status && statusPollRef.current) {
        clearInterval(statusPollRef.current);
        statusPollRef.current = null;
      }
    }, 1000);
  }, [checkServerStatus]);

  useEffect(() => {
    loadData();
    loadStats();
    loadTypes();
    checkServerStatus().then(status => {
      setServerBusy(status);
      if (status) startStatusPoll();
    });
    return () => {
      if (statusPollRef.current) clearInterval(statusPollRef.current);
    };
  }, []);

  useEffect(() => {
    loadData();
  }, [page, typeFilter, activeFilter, sortBy, sortOrder]);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await adminApi.getRegulations({
        page,
        limit: 20,
        search: search || undefined,
        type: typeFilter || undefined,
        isActive: activeFilter,
        sortBy,
        sortOrder,
      });

      if (response.data) {
        setRegulations(response.data.data);
        setTotalPages(response.data.totalPages);
        setTotal(response.data.total);
      }
    } catch (error) {
      const { message } = extractApiError(error);
      showToast(message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await adminApi.getRegulationStats();
      if (response.data) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const loadTypes = async () => {
    try {
      const response = await adminApi.getRegulationTypes();
      if (response.data) {
        setRegulationTypes(response.data);
      }
    } catch (error) {
      console.error('Failed to load types:', error);
    }
  };

  const handleSync = async () => {
    if (syncLoading || serverBusy) return;
    try {
      setSyncLoading(true);
      setServerBusy('sync');
      startStatusPoll();
      const response = await adminApi.syncRegulations();
      if (response.data) {
        const { totalLaws, totalArticles, errors } = response.data;
        showToast(
          `${totalLaws}개 법령, ${totalArticles}개 조문 동기화 완료${errors.length > 0 ? ` (오류 ${errors.length}건)` : ''}`,
          errors.length > 0 ? 'info' : 'success',
        );
        loadData();
        loadStats();
        loadTypes();
      }
    } catch (error) {
      const { message } = extractApiError(error);
      showToast(message, 'error');
    } finally {
      setSyncLoading(false);
      // 서버 상태 다시 확인 (임베딩이 아직 진행 중일 수 있음)
      const status = await checkServerStatus();
      setServerBusy(status);
      if (!status && statusPollRef.current) {
        clearInterval(statusPollRef.current);
        statusPollRef.current = null;
      }
    }
  };

  const handleEmbedLawArticles = async () => {
    if (serverBusy) return;
    try {
      setServerBusy('embedding');
      startStatusPoll();
      const res = await adminApi.embedLawArticles();
      if (res.data) {
        showToast(`LawArticle 임베딩 완료: ${res.data.embedded}건`, 'success');
      }
    } catch (error) {
      const { message } = extractApiError(error);
      showToast(message, 'error');
    } finally {
      const status = await checkServerStatus();
      setServerBusy(status);
      if (!status && statusPollRef.current) {
        clearInterval(statusPollRef.current);
        statusPollRef.current = null;
      }
    }
  };

  const handleEmbedLawSubItems = async () => {
    if (serverBusy) return;
    try {
      setServerBusy('embedding');
      startStatusPoll();
      const res = await adminApi.embedLawSubItems();
      if (res.data) {
        showToast(`LawSubItem 임베딩 완료: ${res.data.embedded}건`, 'success');
      }
    } catch (error) {
      const { message } = extractApiError(error);
      showToast(message, 'error');
    } finally {
      const status = await checkServerStatus();
      setServerBusy(status);
      if (!status && statusPollRef.current) {
        clearInterval(statusPollRef.current);
        statusPollRef.current = null;
      }
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    loadData();
  };

  const fetchRegulationDetail = async (id: string): Promise<Regulation | null> => {
    try {
      const response = await adminApi.getRegulation(id);
      if (response.data) {
        return response.data as Regulation;
      }
    } catch (error) {
      console.error('Failed to fetch regulation detail:', error);
    }
    return null;
  };

  const openDetailModal = async (regulation: Regulation) => {
    const detail = await fetchRegulationDetail(regulation.id);
    setDetailModal(detail || regulation);
  };

  // 배지도 버전 어휘로 — `is_active`는 「켜고 끄는 상태」가 아니라 「현행 시행본인가」다.
  const getActiveBadge = (isActive: boolean) => {
    return (
      <span
        className={`px-2 py-0.5 rounded text-xs font-medium ${
          isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
        }`}
      >
        {isActive ? '현행' : '옛 버전'}
      </span>
    );
  };

  const truncateText = (text: string | null, maxLength: number) => {
    if (!text) return '-';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">법규 관리</h1>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setAnnexesOpen(true)}
            disabled={regulationTypes.length === 0}
          >
            별표 보기
          </Button>
          <Button variant="secondary" size="sm" onClick={() => setAdminRulesOpen(true)}>
            고시 보기
          </Button>
          <Button variant="secondary" size="sm" onClick={() => setUnmappedSymbolsOpen(true)}>
            대응 안 된 기호
          </Button>
          <button
            onClick={handleSync}
            disabled={syncLoading || !!serverBusy}
            className={`px-4 py-2 text-white rounded-control disabled:opacity-50 flex items-center gap-2 ${
              serverBusy === 'embedding'
                ? 'bg-amber-500'
                : serverBusy === 'sync'
                  ? 'bg-brand-600'
                  : 'bg-brand-600 hover:bg-brand-700'
            }`}
          >
            {syncLoading || serverBusy ? (
              <>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                {serverBusy === 'embedding' ? '임베딩 중...' : '업데이트 중...'}
              </>
            ) : (
              '업데이트'
            )}
          </button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleEmbedLawArticles}
            disabled={!!serverBusy}
          >
            조문 임베딩
          </Button>
          <Button
            tone="violet"
            size="sm"
            onClick={handleEmbedLawSubItems}
            disabled={!!serverBusy}
          >
            호 임베딩
          </Button>
        </div>
      </div>

      {/* 통계 카드 */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card radius="lg" elevation="none">
            <div className="text-sm text-gray-500">전체</div>
            <div className="text-2xl font-bold">{stats.total.toLocaleString()}</div>
          </Card>
          <Card radius="lg" elevation="none">
            <div className="text-sm text-gray-500">활성</div>
            <div className="text-2xl font-bold text-green-600">
              {stats.active.toLocaleString()}
            </div>
          </Card>
          <Card radius="lg" elevation="none">
            <div className="text-sm text-gray-500">지식베이스 연결</div>
            <div className="text-2xl font-bold text-blue-600">
              {stats.linkedToKnowledge.toLocaleString()}
            </div>
          </Card>
          <Card radius="lg" elevation="none">
            <div className="text-sm text-gray-500">발견사항 연결</div>
            <div className="text-2xl font-bold text-purple-600">
              {stats.linkedToFindings.toLocaleString()}
            </div>
          </Card>
        </div>
      )}

      {/*
        법령 버전 판정 신호 — **상주**가 요점이다(사용자 확정 2026-10-26).

        왜 패널인가: 같은 수가 수집 응답(`versionReeval`)에도 실리지만 그것은 토스트라 4초 뒤
        사라지고, prod 수집은 cron 03:00에 돌아 **아무도 그 토스트를 보지 않는다**. 이 수는
        `laws` 한 테이블만 보는 집계라 조회마다 다시 셀 수 있으므로 화면에 남긴다.

        ⛔ 두 줄을 **합치지 말 것** — 뜻이 반대고 단위도 다르다.
          「시행 예정」 = 판정이 **내린** 것 · 0이 아닌 것이 **정상** · 단위는 **행**
          「넘긴 것」   = 판정을 **안 한** 것 · 0이 **정상**(0이 아니면 그 수가 다음 작업) ·
                         단위가 **행**(앞 둘)과 **그룹**(뒤 둘)으로 갈려 **더할 수 없다**

        ⛔ 그래서 총계를 쓰지 않고 **라벨에 단위를 박는다**(「… 행 N건」·「… 그룹 N개」 —
        사용자 확정 2026-10-26). 수 뒤에 단위를 붙이는 것만으로는 나란한 네 칸이 **더할 수 있는
        수처럼 보이고**, 그것이 이 레포에서 한 번 겪은 함정이다(나란한 두 지표가 더할 수 없는 단위).

        ⚠️ 「현행 후보 없음」의 사유(예정본만 ↔ 시행일 NULL만 ↔ 둘 다)는 **여기서 가르지 않는다** —
        가르면 둘 다인 그룹이 양쪽에 세어져 분모가 깨진다. 「시행일 없음」 칸과 함께 보면 추론할 수
        있지만 **그 설명문을 화면에 붙이지 않는다**(사용자 결정 2026-10-26 — 그 추론은 「시행일
        NULL이 0행」에 기대므로 화면 문구로 굳히면 그 전제가 깨지는 날 거짓이 된다). 서술의 정본은
        서버 `admin-regulations.service`의 `LawVersionSignals` docblock이다.
      */}
      {stats?.versionSignals &&
        Object.values(stats.versionSignals).some((n) => n > 0) && (
          <Card radius="lg" elevation="none">
            <div className="text-sm font-medium text-gray-700 mb-2">법령 버전 판정 신호</div>
            <div className="space-y-2 text-sm">
              {stats.versionSignals.upcoming > 0 && (
                <div className="flex flex-wrap items-baseline gap-x-2">
                  <span className="font-medium text-gray-900">
                    시행 예정 행 {stats.versionSignals.upcoming.toLocaleString()}건
                  </span>
                  <span className="text-gray-500">
                    현행에서 내렸습니다 — 그 조문은 검색·AI 판정에 쓰이지 않습니다.
                  </span>
                </div>
              )}
              {(stats.versionSignals.skippedNoGroupKey > 0 ||
                stats.versionSignals.skippedNoEnforcementDate > 0 ||
                stats.versionSignals.skippedIncoherentGroups > 0 ||
                stats.versionSignals.skippedNoCurrentCandidate > 0) && (
                <div className="flex flex-wrap items-baseline gap-x-2">
                  <span className="font-medium text-amber-700">판정에서 넘김</span>
                  <span className="text-gray-700">
                    그룹 키(law_id) 없음 행{' '}
                    {stats.versionSignals.skippedNoGroupKey.toLocaleString()}건
                    {' · '}시행일 없음 행{' '}
                    {stats.versionSignals.skippedNoEnforcementDate.toLocaleString()}건
                    {' · '}한 그룹에 이름 둘 이상 그룹{' '}
                    {stats.versionSignals.skippedIncoherentGroups.toLocaleString()}개
                    {' · '}현행 후보 없음 그룹{' '}
                    {stats.versionSignals.skippedNoCurrentCandidate.toLocaleString()}개
                  </span>
                  <span className="text-gray-500">
                    0이 정상입니다 — 이 수가 그대로 다음 작업입니다.
                  </span>
                </div>
              )}
            </div>
          </Card>
        )}

      {/* 필터 */}
      <Card radius="lg" elevation="none">
        <form onSubmit={handleSearch} className="flex flex-wrap gap-4 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">검색</label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="법규명, 조항, 요약 검색..."
              className="w-full px-3 py-2 border rounded-control"
            />
          </div>
          <div className="w-48">
            <label className="block text-sm font-medium text-gray-700 mb-1">법규 유형</label>
            <select
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value);
                setPage(1);
              }}
              className="w-full px-3 py-2 border rounded-control"
            >
              <option value="">전체</option>
              {regulationTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>
          {/*
            「활성/비활성」은 이제 **법령 버전**을 뜻한다 — 같은 법령의 여러 시행본 중 현행
            하나만 `is_active = true`이고 지난 시행본은 false다(2026-10-25 백필·수집 정리).
            그래서 화면 문구를 그 뜻으로 적는다. 「전체」가 사용자 결정의 「옛 버전 보기」다.
          */}
          <div className="w-40">
            <label className="block text-sm font-medium text-gray-700 mb-1">버전</label>
            <select
              value={activeFilter === undefined ? '' : activeFilter ? 'true' : 'false'}
              onChange={(e) => {
                const val = e.target.value;
                setActiveFilter(val === '' ? undefined : val === 'true');
                setPage(1);
              }}
              className="w-full px-3 py-2 border rounded-control"
            >
              <option value="true">현행만</option>
              <option value="">전체(옛 버전 포함)</option>
              <option value="false">옛 버전만</option>
            </select>
          </div>
          <div className="w-32">
            <label className="block text-sm font-medium text-gray-700 mb-1">정렬</label>
            <select
              value={sortBy}
              onChange={(e) => {
                setSortBy(e.target.value as typeof sortBy);
                setPage(1);
              }}
              className="w-full px-3 py-2 border rounded-control"
            >
              <option value="createdAt">생성일</option>
              <option value="type">유형</option>
              <option value="code">법규 코드</option>
              <option value="effectiveDate">시행일</option>
            </select>
          </div>
          <div className="w-24">
            <label className="block text-sm font-medium text-gray-700 mb-1">순서</label>
            <select
              value={sortOrder}
              onChange={(e) => {
                setSortOrder(e.target.value as 'asc' | 'desc');
                setPage(1);
              }}
              className="w-full px-3 py-2 border rounded-control"
            >
              <option value="desc">내림차순</option>
              <option value="asc">오름차순</option>
            </select>
          </div>
          <Button type="submit" variant="primary">
            검색
          </Button>
        </form>
      </Card>

      {/* 테이블 */}
      <Card padding="none" radius="lg" elevation="none" className="overflow-x-auto">
        <table className="min-w-[1200px] w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-48">
                법규 유형
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase min-w-[200px]">
                법규명
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase min-w-[250px]">
                요약
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-20">
                상태
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-24">
                연결 수
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                  로딩 중...
                </td>
              </tr>
            ) : regulations.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                  데이터가 없습니다.
                </td>
              </tr>
            ) : (
              regulations.map((reg) => (
                <tr key={reg.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => openDetailModal(reg)}>
                  <td className="px-4 py-3">
                    <div className="text-sm font-medium text-gray-900">{reg.type}</div>
                    {reg.code && <div className="text-xs text-gray-500">{reg.code}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <div
                      className="text-sm text-gray-900"
                      title={reg.title || undefined}
                    >
                      {truncateText(reg.title, 40)}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm text-gray-600" title={reg.summary || undefined}>
                      {truncateText(reg.summary, 50)}
                    </div>
                  </td>
                  <td className="px-4 py-3">{getActiveBadge(reg.isActive)}</td>
                  <td className="px-4 py-3">
                    <div className="text-xs text-gray-500">
                      <div>지식: {reg.knowledgeLinkCount}</div>
                      <div>발견: {reg.findingLinkCount}</div>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Card>

      {/* 페이지네이션 */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-500">
          총 {total.toLocaleString()}건 중 {(page - 1) * 20 + 1}-{Math.min(page * 20, total)}건
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            이전
          </button>
          <span className="px-3 py-1">
            {page} / {totalPages || 1}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages || totalPages === 0}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            다음
          </button>
        </div>
      </div>

      {/* 상세 모달 */}
      {detailModal && (
        // 종전 패널 스크롤(max-h-[90vh])은 base 본문 스크롤이 맡는다 — 헤더·닫기 줄이 고정된다.
        <Modal
          open
          onClose={() => setDetailModal(null)}
          title="법규 상세"
          size="xl"
          footer={
            <Button variant="secondary" onClick={() => setDetailModal(null)}>
              닫기
            </Button>
          }
        >
            <div>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">법규 유형</label>
                    <div className="mt-1">{detailModal.type}</div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">법규 코드</label>
                    <div className="mt-1">{detailModal.code || '-'}</div>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-500">법규명</label>
                  <div className="mt-1">{detailModal.title || '-'}</div>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-500">요약</label>
                  <div className="mt-1 whitespace-pre-wrap">{detailModal.summary || '-'}</div>
                </div>

                {detailModal.fullText && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">전체 내용</label>
                    <div className="mt-1 p-3 bg-gray-50 rounded-card max-h-64 overflow-y-auto whitespace-pre-wrap text-sm">
                      {detailModal.fullText}
                    </div>
                  </div>
                )}

                <div>
                  <label className="text-sm font-medium text-gray-500">참조 링크</label>
                  <div className="mt-1">
                    {detailModal.url ? (
                      <a
                        href={detailModal.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-brand-600 hover:underline"
                      >
                        {detailModal.url}
                      </a>
                    ) : (
                      '-'
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">시행일</label>
                    <div className="mt-1">
                      {detailModal.effectiveDate
                        ? new Date(detailModal.effectiveDate).toLocaleDateString('ko-KR')
                        : '-'}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">상태</label>
                    <div className="mt-1">{getActiveBadge(detailModal.isActive)}</div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">연결 수</label>
                    <div className="mt-1 text-sm">
                      지식: {detailModal.knowledgeLinkCount} / 발견: {detailModal.findingLinkCount}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm text-gray-500">
                  <div>생성일: {new Date(detailModal.createdAt).toLocaleString('ko-KR')}</div>
                  <div>수정일: {new Date(detailModal.updatedAt).toLocaleString('ko-KR')}</div>
                </div>
              </div>
            </div>
        </Modal>
      )}

      {/* 별표 열람 — 현행 시행본의 별표 원문(S1). 기준값 입력은 법정 의무 카탈로그에서(별표는 알림원) */}
      <LawAnnexesModal
        open={annexesOpen}
        onClose={() => setAnnexesOpen(false)}
        lawNames={regulationTypes}
        initialLawName={typeFilter || undefined}
      />

      {/* 고시 열람 — 법정 의무 판정 규칙이 고시에 있는 것(안전검사 대상 규격 등)의 원문(②-f). 수집 버튼은 두지 않는다(A3 ⓑ) */}
      <AdminRulesModal open={adminRulesOpen} onClose={() => setAdminRulesOpen(false)} />

      {/* 별표 복원의 「대응 안 된 기호」 — 운영 사건이 되지 않는 «상태»라 이 화면이 그것을 보는 자리다(읽기 전용) */}
      <AnnexUnmappedSymbolsModal open={unmappedSymbolsOpen} onClose={() => setUnmappedSymbolsOpen(false)} />

      {/* 동기화/임베딩 진행 중 모달 — 종전처럼 닫는 길이 없다(완료 시 serverBusy가 풀려 사라진다) */}
      {serverBusy && (
        <Modal
          open
          onClose={() => {}}
          ariaLabel={serverBusy === 'sync' ? '법규 동기화 중' : '임베딩 처리 중'}
          size="md"
          closeOnOverlay={false}
          closeOnEsc={false}
          showCloseButton={false}
          closeLocked
        >
            <div className="flex flex-col items-center text-center py-4">
              <svg className="animate-spin h-10 w-10 text-brand-600 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <h3 className="text-lg font-bold text-gray-900 mb-1">
                {serverBusy === 'sync' ? '법규 동기화 중' : '임베딩 처리 중'}
              </h3>
              <p className="text-sm text-gray-500 mb-3">
                {serverBusy === 'sync'
                  ? '국가법령정보에서 법규를 동기화하고 있습니다.'
                  : '법규 임베딩을 생성하고 있습니다.'}
              </p>
              {serverBusy === 'embedding' && embeddingProgress && embeddingProgress.total > 0 && (
                <div className="w-full">
                  <div className="w-full bg-gray-100 rounded-full h-2 mb-2">
                    <div
                      className="bg-indigo-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${Math.round((embeddingProgress.processed / embeddingProgress.total) * 100)}%` }}
                    />
                  </div>
                  <div className="text-sm text-gray-600">
                    {embeddingProgress.processed.toLocaleString()} / {embeddingProgress.total.toLocaleString()}
                  </div>
                </div>
              )}
              {serverBusy === 'embedding' && (!embeddingProgress || embeddingProgress.total === 0) && (
                <p className="text-xs text-gray-400">준비 중...</p>
              )}
              <p className="text-xs text-gray-400 mt-2">완료 시 자동으로 닫힙니다.</p>
            </div>
        </Modal>
      )}

    </div>
  );
}
